import {
  CruiseLiteModel,
  CruiseRoomItemModel,
  CruiseShipsItemModel,
} from 'api-hooks/cruise';
import { EmployeeLiteModel } from 'api-hooks/employee';
import { GuestLiteModel } from 'api-hooks/guest';
import { Expose, Type } from 'class-transformer';
import {
  CommonModel,
  CommonParamInput,
} from 'common/repositories/common.model';

import { PurchaseModel } from '../common/model';

// Model

export class CruisePurchaseAddOnsItemModel extends CommonModel {
  description: string;
  route?: string | null;

  @Type(() => Number)
  price: number;

  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;
}

export class CruisePurchaseGuestItemModel extends CommonModel {
  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;
}

export class CruisePurchaseDetailsModel extends CommonModel {
  @Expose({ name: 'basic_fare' })
  @Type(() => Number)
  basicFare: number;

  @Expose({ name: 'cruise_purchase_guest_id' })
  cruisePurchaseGuestId?: string | null;

  @Expose({ name: 'cruise_room' })
  @Type(() => CruiseRoomItemModel)
  cruiseRoom: CruiseRoomItemModel;

  @Expose({ name: 'cruise_room_number' })
  cruiseRoomNumber: string;

  deck: string;
  description: string;

  @Expose({ name: 'port_charge' })
  @Type(() => Number)
  portCharge: number;

  status: string;

  @Type(() => Number)
  tax: number;

  @Type(() => Number)
  tipping: number;
}

export class CruisePurchaseItemModel extends CommonModel {
  route: string;

  @Expose({ name: 'booking_code' })
  bookingCode?: string | null;

  @Expose({ name: 'cruise_ship' })
  @Type(() => CruiseShipsItemModel)
  cruiseShip: CruiseShipsItemModel;

  @Type(() => Boolean)
  reminder: boolean;

  @Type(() => CruiseLiteModel)
  cruise: CruiseLiteModel;

  @Expose({ name: 'is_grouped_price' })
  @Type(() => Boolean)
  isGroupedPrice: boolean;

  @Expose({ name: 'started_at' })
  @Type(() => Date)
  startedAt: Date;

  @Expose({ name: 'ended_at' })
  @Type(() => Date)
  endedAt: Date;

  @Expose({ name: 'cruise_purchase_guests' })
  @Type(() => CruisePurchaseGuestItemModel)
  cruisePurchaseGuests: CruisePurchaseGuestItemModel[];

  @Expose({ name: 'cruise_purchase_details' })
  @Type(() => CruisePurchaseDetailsModel)
  cruisePurchaseDetails: CruisePurchaseDetailsModel[];

  @Expose({ name: 'cruise_purchase_add_ons' })
  @Type(() => CruisePurchaseAddOnsItemModel)
  cruisePurchaseAddOns: CruisePurchaseAddOnsItemModel[];
}

export class CruisePurchaseLiteModel extends CommonModel {
  status: string;
  description: string;
  code: string;
  number: string;
  agent: string;

  @Expose({ name: 'created_by' })
  @Type(() => EmployeeLiteModel)
  createdBy: EmployeeLiteModel;

  @Expose({ name: 'payment_channel' })
  paymentChannel: string;

  @Expose({ name: 'invoice_number' })
  invoiceNumber: string;

  @Expose({ name: 'transaction_at' })
  @Type(() => Date)
  transactionAt: Date;

  @Type(() => Number)
  total: number;
}

export class CruisePurchaseModel extends PurchaseModel {
  @Expose({ name: 'cruise_purchases' })
  @Type(() => CruisePurchaseItemModel)
  cruisePurchases: CruisePurchaseItemModel[];
}

export class CruisePurchaseGuestDetailModel {
  id: string;

  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;
}

export class CruisePurchaseDetailModel {
  id: string;
  route: string;

  @Expose({ name: 'booking_code' })
  bookingCode?: string | null;

  @Type(() => CruiseLiteModel)
  cruise: CruiseLiteModel;

  @Expose({ name: 'ended_at' })
  @Type(() => Date)
  endedAt: Date;

  @Expose({ name: 'started_at' })
  @Type(() => Date)
  startedAt: Date;

  @Expose({ name: 'is_grouped_price' })
  @Type(() => Boolean)
  isGroupedPrice: boolean;

  @Expose({ name: 'cruise_purchase_add_ons' })
  @Type(() => CruisePurchaseAddOnsItemModel)
  cruisePurchaseAddOns: CruisePurchaseAddOnsItemModel[];

  @Expose({ name: 'cruise_purchase_guests' })
  @Type(() => CruisePurchaseGuestDetailModel)
  cruisePurchaseGuests: CruisePurchaseGuestDetailModel[];

  @Expose({ name: 'cruise_purchase_details' })
  @Type(() => CruisePurchaseDetailsModel)
  cruisePurchaseDetails: CruisePurchaseDetailsModel[];

  @Expose({ name: 'cruise_ship' })
  @Type(() => CruiseShipsItemModel)
  cruiseShip: CruiseShipsItemModel;
}

// Input

export type getCruisePurchasesInput = {
  params: CommonParamInput;
};

export type getCruisePurchaseInput = {
  id: string;
};

export type CruisePurchaseGuestItemInput = {
  id?: string | null;
  guestId: string | null;
};

export type CruisePurchaseDetailsItemInput = {
  id?: string | null;
  guestId: string | null;
  cruiseRoomId: string | null;
  basicFare: number;
  portCharge: number;
  tax: number;
  tipping: number;
  description: string;
  deck: string | null;
  cruiseRoomNumber: string | null;
};

export type CruisePurchaseAddOnsItemInput = {
  id?: string | null;
  guestId: string | null;
  description: string;
  price: number;
};

export type CruisePurchaseItemInput = {
  id?: string | null;
  cruiseId: string | null;
  startedAt: Date | null;
  endedAt: Date | null;
  route: string;
  isGroupedPrice: boolean;
  reminder: boolean;
  bookingCode?: string | null;

  cruisePurchaseGuests: CruisePurchaseGuestItemInput[];
  cruisePurchaseDetails: CruisePurchaseDetailsItemInput[];
  cruisePurchaseAddOns: CruisePurchaseAddOnsItemInput[];
};

export type CruisePurchaseMutationInput = {
  //Required - Detail
  code: string;
  description: string;
  type?: string | null;
  transactionAt: Date | null;
  needApproval: boolean;
  tourRefId?: string | null;
  invoiceRefId?: string | null;
  agentId?: string | null;
  paymentChannelId: string | null;
  cruisePurchases: CruisePurchaseItemInput[];
  discount: number;
  otherCost: number;
  total: number;

  // Not Required
  orderNumber?: string | null;
  refId?: string | null;
  parentRefId?: string | null;
};

export type CruisePurchaseUpdateInput = {
  cruisePurchaseId: string;
  body: CruisePurchaseMutationInput;
};

export type CruisePurchaseDeleteInput = {
  cruisePurchaseId: string;
};
